import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import { payrollAgreementFilters } from './payroll-agreement.filters';
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela do convênio para executar esta ação.`);
};
export const PAYROLL_AGREEMENT_PATH = '/cadastro/convenios';
export const payrollAgreementPath = (id: string) =>
  `${PAYROLL_AGREEMENT_PATH}/${encodeURIComponent(id)}`;
export const payrollAgreementCreate = action({
  id: 'payrollAgreement.create',
  kind: 'draft',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco para cadastrar um convênio de crédito.`,
  permission: can('ManagePayrollAgreementSettings', 'Create'),
  run: ({ nav }) => nav.to(payrollAgreementPath('novo')),
});
export const payrollAgreementOpen = action({
  id: 'payrollAgreement.open',
  kind: 'navigate',
  label: msg`Editar`,
  description: msg`Abre o cadastro de um convênio pelo id. Quem só pode consultar vê o cadastro em modo de leitura.`,
  row: true,
  page: true,
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(payrollAgreementPath(input.id)),
});
export const payrollAgreementDelete = action({
  id: 'payrollAgreement.delete',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Exclui um convênio pelo id, de vez. Não dá para desfazer. Pede a confirmação da pessoa.`,
  row: true,
  permission: can('ManagePayrollAgreementSettings', 'Delete'),
  input: z.object({ id: z.string().min(1), name: z.string().optional() }),
  preview: ({ id }) => ({
    title: t`Excluir convênio`,
    detail: t`Excluir o convênio ${id}? Esta ação não pode ser desfeita.`,
    tone: 'destructive',
  }),
  run: mountedScreenRequired,
});
export const payrollAgreementSaveInput = z.object({
  values: z.record(z.string(), z.unknown()).optional().describe(
    // eslint-disable-next-line lingui/no-unlocalized-strings -- contrato técnico da ferramenta
    'Optional patch of editable form fields. Use minimumDownPaymentValueInCents for integer cents; percentages are fractions.',
  ),
});
export const payrollAgreementSave = action({
  id: 'payrollAgreement.save',
  kind: 'write',
  label: msg`Salvar`,
  description: msg`Confere e salva o convênio aberto, com o que está no formulário. Aceita mudar antes alguns campos editáveis. Pede a confirmação da pessoa.`,
  input: payrollAgreementSaveInput,
  preview: () => ({
    title: t`Salvar convênio`,
    detail: t`Confira os dados do convênio antes de salvar.`,
  }),
  run: mountedScreenRequired,
});
export const payrollAgreementRefresh = action({
  id: 'payrollAgreement.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados do convênio aberto, sem mudar nada.`,
  run: mountedScreenRequired,
});
export const payrollAgreementHistory = action({
  id: 'payrollAgreement.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o painel com o histórico de alterações do convênio aberto.`,
  run: mountedScreenRequired,
});
export const payrollAgreementListRefresh = action({
  id: 'payrollAgreement.list.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a lista de convênios de novo, sem mudar nada.`,
  run: mountedScreenRequired,
});
export const payrollAgreementBack = action({
  id: 'payrollAgreement.back',
  kind: 'navigate',
  label: msg`Convênios`,
  description: msg`Volta para a lista de convênios.`,
  run: ({ nav }) => nav.to(PAYROLL_AGREEMENT_PATH),
});
export const payrollAgreementFilter = filterAction(
  'payrollAgreement.filter',
  payrollAgreementFilters,
  { description: msg`Filtra a lista de convênios: searchString (busca livre), name (nome do convênio) e initialDate/finalDate (data de criação, AAAA-MM-DD). Filtro omitido é limpo.` },
);
export const payrollAgreementListPage = listPageAction('payrollAgreement.list.page');
export const payrollAgreementListScreen = defineScreen({
  id: 'payrollAgreement.list',
  path: PAYROLL_AGREEMENT_PATH,
  title: msg`Convênios de Crédito`,
  description: msg`Lista de convênios de crédito: busca, filtro por data de criação, paginação, criar, editar e excluir.`,
  place: { section: 'register', icon: 'handshake' },
  actions: [
    payrollAgreementCreate,
    payrollAgreementListRefresh,
    payrollAgreementFilter,
    payrollAgreementListPage,
    payrollAgreementOpen,
    payrollAgreementDelete,
  ],
});
export const payrollAgreementDetailScreen = defineScreen({
  id: 'payrollAgreement.detail',
  path: `${PAYROLL_AGREEMENT_PATH}/$convenioId`,
  title: msg`Convênio de Crédito`,
  description: msg`Cadastro de um convênio de crédito (novo ou edição) em quatro seções: informações básicas, sistema de averbação, regras de negócio e categorias de trabalhador; salvar, atualizar e histórico.`,
  place: { parent: 'payrollAgreement.list', icon: 'file-text', record: msg`Convênio` },
  actions: [
    payrollAgreementBack,
    payrollAgreementRefresh,
    payrollAgreementSave,
    payrollAgreementHistory,
  ],
});
